import { type RouteEstimateResponseType } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";

import { computeTargetArrivalTime } from "./computeTargetArrivalTime";
import { getDepartureTimeForShift } from "./getDepartureTimeForShift";

/**
 * Computed arrival data that contains all the processed values from a route estimate.
 * This eliminates the need to repeatedly convert RouteEstimateResponseType objects.
 */
export interface ComputedArrivalData {
  /** The calculated target arrival time as a Date object */
  targetArrivalTime: Date;
  /** The calculated departure time as a Date object */
  departureTime: Date;
  /** Duration in minutes from the route estimate */
  durationInMinutes: number;
  /** Distance in meters from the route estimate */
  distanceInMeters: number;
  /** Provider used for the route estimate */
  provider: "GOOGLE_MAPS" | "AWS_LOCATION" | "SHIFT_START";
  /** Grace period in minutes used for calculations */
  gracePeriodInMinutes: number;
  /** ISO string of the shift start time used for calculations */
  shiftStartIso: string;
}

/**
 * Parameters needed to compute arrival data from a route estimate
 */
export interface ComputeArrivalDataParams {
  routeEstimate: RouteEstimateResponseType;
  shiftStartIso: string;
  gracePeriodInMinutes: number;
  shiftAuctionEndDate?: Date;
}

/**
 * Computes all arrival-related data from a route estimate once.
 * This eliminates the need for repeated conversions throughout the app.
 */
export function computeArrivalData(
  params: ComputeArrivalDataParams
): ComputedArrivalData | undefined {
  const { routeEstimate, shiftStartIso, gracePeriodInMinutes, shiftAuctionEndDate } = params;

  if (!isDefined(routeEstimate?.data?.attributes?.eta) || !isDefined(shiftStartIso)) {
    return undefined;
  }

  const { eta } = routeEstimate.data.attributes;
  const shiftStartDate = new Date(shiftStartIso);

  // Compute target arrival time
  const targetArrivalTime = computeTargetArrivalTime({
    shiftStart: shiftStartDate,
    etaInMinutes: eta.durationInTrafficMinutes,
    gracePeriodInMinutes,
    shiftAuctionEndDate,
  });

  // Compute departure time using the same logic as the old useDepartureTime hook
  const departureTime = getDepartureTimeForShift({
    targetArrivalTime: targetArrivalTime.toISOString(),
    durationInMinutes: eta.durationInTrafficMinutes,
  });

  return {
    targetArrivalTime,
    departureTime,
    durationInMinutes: eta.durationInTrafficMinutes,
    distanceInMeters: eta.distanceMeters,
    provider: eta.provider,
    gracePeriodInMinutes,
    shiftStartIso,
  };
}
